import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Checkbox,
  Link as DSLink,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodControls,
  type SodControlsRef,
  SodSort,
} from '../../../../shared/components';
import SodColumnsList from '../../../../shared/components/SodColumnsList';
import {
  ABORT_ERROR,
  CF_NEU_MED,
  DEFAULT_AD_FILTER_PARAMS,
  RENDER_MODES,
  TABS_FILTERS,
  WIDGETS,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../../shared/types';
import { detailsActions } from '../../../Details/data-store/detailsSlice';
import { useLazyGetReportColumnsQuery } from '../../../ViolationsReport/data-layer/violationsReportApi';
import { violationsReportActions } from '../../../ViolationsReport/data-store/violationsReportSlice';
import { type ColumnState } from '../../data-layer/violationsTabTypes';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { useFilterCache, useGetQueryMap, useGetSortMenu } from '../../hooks';

import './ViolationsControls.css';

interface ViolationsControlsProps {
  isStickyControls?: boolean;
}

/**
 * ViolationsControls
 */
function ViolationsControls({
  isStickyControls,
}: ViolationsControlsProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const {
    aag: {
      global: { isReadOnlyAction },
      violationsTab: {
        controls: {
          filters: mainFilters,
          tabFilter: mainTabFilter,
          filterCache,
          activeFilters: mainActiveFilters,
          isFilterBarOpen,
          renderMode,
          isInsightsOpen,
          activeColumns,
          sortInAction,
          activeInsight,
          activeGraphPoint,
          activeRowContext,
          activeWidgetItem,
          tabInAction,
          searchKey,
        },
        list: { totalCount, isListLoading },
        bulkaction: { isBulkChecked, isFooterVisible },
      },
      details: {
        entitlementViolationsTab: {
          activeFilters: entitlementActiveFilters,
          tabFilter: entitlementTabFilter,
        },
      },
    },

    user: { isMenuPinned },
  } = useTypedSelector((state) => state);

  // Detect entitlement context from URL
  const isEntitlementContext = window.location.pathname.includes('entitlement');

  // Use entitlement state when in entitlement context
  const filters = mainFilters;
  const tabFilter = isEntitlementContext ? entitlementTabFilter : mainTabFilter;
  const activeFilters = isEntitlementContext
    ? entitlementActiveFilters
    : mainActiveFilters;

  // Hook for sort functionality
  const {
    fields,
    fieldName,
    actions,
    actionName,
    handleFieldClick,
    handleActionClick,
  } = useGetSortMenu(sortInAction);
  //  Hooks for filter
  const controllerRef = useRef<SodControlsRef | null>(null);
  const permissionsColumnsFetchedRef = useRef(false);
  const queryMap = useGetQueryMap();
  const [triggerGetReportColumns] = useLazyGetReportColumnsQuery();
  const { getFromCache, setCache } = useFilterCache(
    filterCache,
    violationsTabActions.setFiltersCache
  );

  // Constants
  const isListMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.LIST),
    [renderMode]
  );
  const isSticky = useMemo(
    () => Boolean(isStickyControls && !isListMode),
    [isStickyControls, isListMode]
  );
  const isClearBtn = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );
  const isInsightsDisabled = useMemo(
    () =>
      Boolean(isFooterVisible || isSticky) ||
      Boolean(
        isListMode && (activeGraphPoint ?? activeRowContext ?? activeWidgetItem)
      ),
    [
      isFooterVisible,
      isSticky,
      activeGraphPoint,
      activeRowContext,
      activeWidgetItem,
      isListMode,
    ]
  );
  const isCheckboxDisabled = useMemo(
    () =>
      Boolean(
        tabInAction === TABS_FILTERS.REMEDIATEDCOUNT.value ||
          tabInAction === TABS_FILTERS.CLOSEDCOUNT.value ||
          isListLoading ||
          totalCount === null ||
          totalCount === 0 ||
          isReadOnlyAction
      ),
    [isListLoading, tabInAction, totalCount, isReadOnlyAction]
  );
  const isSortDisabled = useMemo(
    () =>
      Boolean(
        isListLoading || activeGraphPoint !== null || activeWidgetItem !== null
      ),
    [activeGraphPoint, activeWidgetItem, isListLoading]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one time default search value from state
  const defaultSearchValue = useMemo(() => searchKey ?? undefined, []);

  const entitlementSetDelta = useCallback(
    (oldFilters: unknown[], newFilters: unknown[]) => {
      // Always preserve disabled filters (entitlement_value)
      const preservedFilters = (oldFilters as ActiveFiltersState[]).filter(
        (filter) =>
          filter.value === 'entitlement_value' &&
          filter.options.some((option) => option.isDisabled)
      );

      // Remove preserved filters from newFilters to avoid duplicates
      const preservedValues = preservedFilters.map((f) => f.value);
      let otherNewFilters = (newFilters as ActiveFiltersState[]).filter(
        (filter) => !preservedValues.includes(filter.value)
      );

      // In list mode, exclude riskName filters only when clearing (when newFilters has fewer items)
      const isClearing =
        (newFilters as ActiveFiltersState[]).length <
        (oldFilters as ActiveFiltersState[]).length;

      if (isListMode && isClearing) {
        otherNewFilters = otherNewFilters.filter(
          (filter) => filter.value !== 'riskName'
        );
      }

      const finalFilters = [...preservedFilters, ...otherNewFilters];

      dispatch(
        detailsActions.setEntitlementViolationsActiveFilters(finalFilters)
      );
    },
    [dispatch, isListMode]
  );

  // For Filters
  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      const cacheResponse = getFromCache(key, searchText);

      if (cacheResponse) {
        return cacheResponse;
      }

      // Abort shoild only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        const fn = queryMap[key];

        const controller = fn({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
          key,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response as MenuOption[];

        setCache(key, searchText, options);

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        Logger.log('error', err.message);

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [queryMap, getFromCache, setCache]
  );

  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      // Check if filter is cleared
      if (selectedOptions.length === 0) {
        const newActiveFilters = activeFilters.filter(
          (filter: { value: string }) => filter.value !== key
        );

        if (isEntitlementContext) {
          entitlementSetDelta(activeFilters, newActiveFilters);

          return;
        }

        dispatch(violationsTabActions.setActiveFilters(newActiveFilters));

        return;
      }

      const newActiveFilters = activeFilters.some(
        (f: { value: string }) => f.value === key
      )
        ? activeFilters.map((f: { value: string; options: unknown[] }) =>
            f.value === key ? { ...f, options: selectedOptions } : f
          )
        : [
            ...(activeFilters as unknown[]),
            { value: key, method, options: selectedOptions },
          ];

      if (isEntitlementContext) {
        entitlementSetDelta(activeFilters, newActiveFilters);

        return;
      }

      dispatch(
        violationsTabActions.setActiveFilters(
          newActiveFilters as ActiveFiltersState[]
        )
      );
    },
    [activeFilters, dispatch, entitlementSetDelta, isEntitlementContext]
  );

  const handleFilterChange = useCallback(
    (
      selectedFilter: Pick<
        AdvancedFilterState,
        'value' | 'isChecked' | 'method'
      >
    ) => {
      const isFilterApplied = activeFilters.find(
        (filter: { value: string }) => filter.value === selectedFilter.value
      );

      if (selectedFilter.isChecked && isFilterApplied) {
        handleOptionsClick(selectedFilter.value, selectedFilter.method, []);
      }

      const modifiedFilters = (
        filters as { value: string; isChecked: boolean }[]
      ).map((filter) => {
        if (selectedFilter.value === filter.value) {
          return {
            ...filter,
            isChecked: !filter.isChecked,
          };
        }

        return filter;
      });

      if (isEntitlementContext) {
        dispatch(
          detailsActions.setEntitlementViolationsFilters(
            modifiedFilters as AdvancedFilterState[]
          )
        );
      } else {
        dispatch(
          violationsTabActions.setAdvancedFilters(
            modifiedFilters as AdvancedFilterState[]
          )
        );
      }
    },
    [activeFilters, filters, isEntitlementContext, handleOptionsClick, dispatch]
  );

  const handleClearBtn = useCallback(() => {
    if (isEntitlementContext) {
      // For entitlement context, preserve disabled filters when clearing
      const preservedFilters = activeFilters.filter(
        (filter: ActiveFiltersState) =>
          filter.value === 'entitlement_value' &&
          filter.options.some((option) => option.isDisabled)
      );

      entitlementSetDelta(activeFilters, preservedFilters);

      return;
    }

    dispatch(violationsTabActions.setActiveFilters([]));
  }, [isEntitlementContext, dispatch, activeFilters, entitlementSetDelta]);

  useEffect(() => {
    if (isFilterBarOpen) {
      controllerRef.current?.openFilterBar();

      dispatch(violationsTabActions.setIsFilterBarOpen(false));
    }
  }, [dispatch, isFilterBarOpen]);

  // For sidepanel and insights aside
  const handleInsights = useCallback(() => {
    dispatch(violationsTabActions.setIsInsightsOpen(!isInsightsOpen));
  }, [isInsightsOpen, dispatch]);

  const handleModeChange = useCallback(
    (mode: number) => {
      if (mode !== 1) {
        // If graph cluster is selected, then close the insights aside
        batch(() => {
          if (activeRowContext) {
            dispatch(violationsTabActions.setGraphRowContext(null));
          }

          if (activeGraphPoint) {
            dispatch(violationsTabActions.setIsInsightsOpen(false));
          }

          dispatch(violationsTabActions.setRenderMode(RENDER_MODES.LIST));
        });

        // Fetch PERMISSIONS report columns once per session when switching to list view
        if (!permissionsColumnsFetchedRef.current) {
          permissionsColumnsFetchedRef.current = true;
          triggerGetReportColumns('PERMISSIONS')
            .unwrap()
            .then((response) => {
              dispatch(
                violationsReportActions.setColumnsForReportType({
                  reportType: 'PERMISSIONS',
                  columns: response,
                })
              );
            })
            .catch(() => {
              // Fail silently; do not retry this session
            });
        }

        return;
      }

      dispatch(violationsTabActions.setRenderMode(RENDER_MODES.GRAPH));
    },
    [dispatch, activeGraphPoint, activeRowContext, triggerGetReportColumns]
  );

  // For tabs
  const handleTabChange = useCallback(
    (value: string) => {
      dispatch(violationsTabActions.setTabInAction(value));
    },
    [dispatch]
  );

  // For search
  const handleSearch = useCallback(
    (searchText: string) => {
      dispatch(violationsTabActions.setSearchKey(searchText));
    },
    [dispatch]
  );

  // For column filter
  const handleColumnsChange = useCallback(
    (columns: ColumnState[]) => {
      dispatch(violationsTabActions.setViewColumns(columns));
    },
    [dispatch]
  );

  // For selected insights or graph cluster
  const renderActiveKind = useCallback(() => {
    if (!isListMode) {
      return null;
    }

    if (activeWidgetItem) {
      return (
        <Typography
          kind='body2'
          color={CF_NEU_MED}
          className='Sod-ViolationsControls-activeKindWrapper--isWidgetItem'>
          {l('Displaying items related to the widget: ')}
          <span className='Sod-ViolationsControls-activeKindBoldText'>
            {activeWidgetItem.data.name}
            {l(WIDGETS[activeWidgetItem.value].label)}
          </span>
          <DSLink
            text={l('Clear')}
            kind='button'
            onClick={() => {
              dispatch(violationsTabActions.setActiveWidgetItem(null));
            }}
          />
        </Typography>
      );
    }

    if (activeGraphPoint) {
      const graphText = `${l('Displaying violations selected in the graph.')} `;

      return (
        <Typography
          kind='body2'
          color={CF_NEU_MED}
          className='Sod-ViolationsControls-activeKindWrapper--isGraphCluster'>
          {graphText}
          <DSLink
            text={l('Clear')}
            kind='button'
            onClick={() => {
              dispatch(violationsTabActions.setActiveGraphPoint(null));
            }}
          />
        </Typography>
      );
    }

    if (activeInsight) {
      return (
        <Typography
          kind='body2'
          color={CF_NEU_MED}
          className='Sod-ViolationsControls-activeKindWrapper--isInsight'>
          {l('Displaying items related to the insight: ')}
          <span className='Sod-ViolationsControls-activeKindLinkText'>
            {activeInsight.title}
          </span>
        </Typography>
      );
    }

    return null;
  }, [
    l,
    dispatch,
    isListMode,
    activeInsight,
    activeGraphPoint,
    activeWidgetItem,
  ]);

  const wrapperClasses = cx(
    'Sod-ViolationsControls',
    isSticky && 'Sod-ViolationsControls--isSticky',
    isMenuPinned && 'Sod-ViolationsControls--isMenuPinned'
  );

  return (
    <Box className={wrapperClasses} dataTheme={isSticky ? 'light' : null}>
      <SodControls
        ref={controllerRef}
        tabs={tabFilter}
        filters={filters}
        activeFilters={activeFilters}
        maxEnabledFilterCount={12}
        searchPlaceholder={l('Search')}
        defaultSearchValue={defaultSearchValue}
        isClearBtn={isClearBtn}
        isInsightsDisabled={isInsightsDisabled}
        isInsightsOpen={isInsightsOpen}
        currentMode={isListMode ? 2 : 1}
        setIsInsightsOpen={handleInsights}
        handleFilterChange={handleFilterChange}
        handleFilterSearch={handleFilterSearch}
        handleOptionsClick={handleOptionsClick}
        handleClearBtn={handleClearBtn}
        handleTabChange={handleTabChange}
        handleModeChange={handleModeChange}
        handleSearch={handleSearch}
        isDisabled={isListLoading}
        isDarkTheme={!isSticky && !isListMode}
      />
      {renderActiveKind()}
      {isListMode && (
        <Box className='Sod-ViolationsControls-groupControls'>
          <Checkbox
            showHoverEffect
            isChecked={isBulkChecked}
            onChange={() => {
              dispatch(violationsTabActions.setIsBulkChecked(!isBulkChecked));
            }}
            isDisabled={isCheckboxDisabled}
            textPosition='after'
            size='small'
            label={l('Select All ({count})', { count: totalCount ?? '-' })}
          />
          <Box className='Sod-ViolationsControls-groupControlsArrangeBtns'>
            <SodSort
              fields={fields}
              fieldName={fieldName ? l(fieldName) : ''}
              actions={actions}
              actionName={actionName ? l(actionName) : ''}
              handleFieldClick={handleFieldClick}
              handleActionClick={handleActionClick}
              isDisabled={isSortDisabled}
            />
            <Box
              tag='span'
              className='Sod-ViolationsControls-groupControlsArrangeBtnsDivider'
            />
            <SodColumnsList
              columns={activeColumns}
              isDisabled={isListLoading}
              handleColumnsChange={handleColumnsChange}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

ViolationsControls.defaultProps = {
  isStickyControls: false,
};

export default memo(ViolationsControls);
